import React, { useState, useCallback, useEffect } from 'react';
import { Card, Button, Form, InputGroup, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../hooks/useRedux';
import { submitInvite, clearInviteState } from '../features/invite/invite.slice';
import { setSelectedFreelancersForInvoice } from '../features/customer/customer.slice';
import './collective-invoice.scss';

interface Friend {
  id: string;
  userId?: string | number; // Add userId field
  name: string;
  email: string;
}

interface CollectiveInvoiceProps {
  onCollectiveInvoiceClick?: () => void;
  copiedInvoiceData?: any;  // NEW - Optional copied invoice data
  isEditModeInvoice?: boolean;  // NEW - Edit mode flag
}

const CollectiveInvoice: React.FC<CollectiveInvoiceProps> = ({ onCollectiveInvoiceClick, copiedInvoiceData, isEditModeInvoice }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitedFriends, setInvitedFriends] = useState<string[]>([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const searchWrapperRef = React.useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const customerState = useAppSelector((state: any) => state?.customer || {});
  const { inviteData, loading: inviteLoading, error } = useAppSelector((state) => state?.invite || { inviteData: null, loading: false, error: null });
  const { customers = [], loading: customerLoading = false, error: customerError = null, selectedCustomer, freelancers = [] } = customerState;

  const [friends, setFriends] = useState<Friend[]>([]);
  const [selectedFreelancerIds, setSelectedFreelancerIds] = useState<Set<string>>(new Set());
  const [showSearchError, setShowSearchError] = useState(false);
  // Check if customer is selected
  const isCustomerSelected = !!selectedCustomer;

  // Handle copiedInvoiceData and auto-select freelancers
  useEffect(() => {
    if (copiedInvoiceData && copiedInvoiceData.invoiceFreelancer) {
      
      const invoiceFreelancers = copiedInvoiceData.invoiceFreelancer;
      
      if (Array.isArray(invoiceFreelancers) && invoiceFreelancers.length > 0) {
        // Skip index 0, auto-select rest of the freelancers
        const freelancersToSelect = invoiceFreelancers.slice(1); // Skip first one (index 0)    
        const selectedFriends: Friend[] = [];
        
        freelancersToSelect.forEach((invoiceFreelancer: any) => {
          const freelancerDetails = invoiceFreelancer.freelancerDetails;
          
          if (freelancerDetails) {
            // Check if this freelancer exists in the available freelancers list
            const matchingFreelancer = freelancers.find((f: any) => 
              f.signUpUser?.userId === freelancerDetails.userId || 
              f.signUpUser?.email === freelancerDetails.email ||
              f.userId === freelancerDetails.userId
            );
            
            if (matchingFreelancer) {
              const signUpUser = matchingFreelancer.signUpUser || {};
              const friend: Friend = {
                id: matchingFreelancer.id?.toString() || invoiceFreelancer.userId?.toString() || Date.now().toString(),
                userId: invoiceFreelancer.userId,
                name: `${freelancerDetails.firstName || ''} ${freelancerDetails.lastName || ''}`.trim() || freelancerDetails.email || 'Unknown User',
                email: freelancerDetails.email || ''
              };
              
              // Check if not already added
              if (!friends.some(f => f.email === friend.email)) {
                selectedFriends.push(friend);  }
            } 
          }
        });
        
        if (selectedFriends.length > 0) {
          setFriends(prevFriends => [...prevFriends, ...selectedFriends]);
         }
      }
    }
  }, [copiedInvoiceData, freelancers]);

  // Auto-select freelancer at index 0 when customer is selected and freelancers are available
  useEffect(() => {
    if (isCustomerSelected && freelancers.length > 0) {
      const firstFreelancer = freelancers[0];
      const signUpUser = firstFreelancer.signUpUser || {};
      const firstFriend: Friend = {
        id: firstFreelancer.id?.toString() || '0',
        userId: firstFreelancer.userId || signUpUser.userId || signUpUser.id, // Add userId
        name: `${signUpUser.firstName || ''} ${signUpUser.lastName || ''}`.trim() || signUpUser.email || 'Unknown User',
        email: signUpUser.email || ''
      };
      // Only add if not already in friends
      if (!friends.some(f => f.id === firstFriend.id)) {
        setFriends(prevFriends => [firstFriend, ...prevFriends]);
      }
    }
  }, [isCustomerSelected, freelancers]);

  // Sync friends to Redux whenever they change
  useEffect(() => {
    dispatch(setSelectedFreelancersForInvoice(friends));
  }, [friends, dispatch]);

  // Sync from Redux back to friends when selectedFreelancersForInvoice changes externally
  // (e.g., when a freelancer is removed from item-details component)
  useEffect(() => {
    const selectedFreelancers = customerState.selectedFreelancersForInvoice || [];
    
    // Only sync if there's a meaningful difference
    if (selectedFreelancers.length !== friends.length) {
      // Convert selectedFreelancersForInvoice to friends format
      const updatedFriends: Friend[] = selectedFreelancers.map((freelancer: any) => ({
        id: freelancer.id?.toString() || freelancer.userId?.toString() || '',
        userId: freelancer.userId || freelancer.id,
        name: freelancer.name || freelancer.userName || `${freelancer.firstName || ''} ${freelancer.lastName || ''}`.trim() || freelancer.email || 'Unknown User',
        email: freelancer.email || ''
      }));
      
      setFriends(updatedFriends);
    }
  }, [customerState.selectedFreelancersForInvoice]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter freelancers based on search value
  const filteredFreelancers = freelancers.filter((freelancer: any) => {
    if (!searchValue.trim()) return false;
    const searchTerm = searchValue.toLowerCase();
    const signUpUser = freelancer.signUpUser || {};
    return (
      signUpUser.firstName?.toLowerCase().includes(searchTerm) ||
      signUpUser.lastName?.toLowerCase().includes(searchTerm) ||
      signUpUser.email?.toLowerCase().includes(searchTerm) ||
      `${signUpUser.firstName || ''} ${signUpUser.lastName || ''}`.toLowerCase().includes(searchTerm)
    );
  });

  // Show suggestions when focused and no search value, or when there are filtered results
  const shouldShowSuggestions = showSuggestions && (
    (searchValue.trim() === '' && freelancers.length > 0) ||
    (searchValue.trim() !== '' && filteredFreelancers.length > 0)
  );

  // Get suggestions to display - filter out already added friends AND the freelancer at index 0
  const availableFreelancers = freelancers.filter((freelancer: any, index: number) => {
    // Hide freelancer at index 0
    if (index === 0) return false;
    
    const freelancerEmail = freelancer.signUpUser?.email;
    return !friends.some(friend => friend.email === freelancerEmail);
  });

  const availableFilteredFreelancers = filteredFreelancers.filter((freelancer: any) => {
    // Hide freelancer at index 0
    if (freelancers.length > 0 && freelancer.id === freelancers[0].id) return false;
    
    const freelancerEmail = freelancer.signUpUser?.email;
    return !friends.some(friend => friend.email === freelancerEmail);
  });

  const suggestionsToShow = searchValue.trim() === ''
    ? availableFreelancers // Show all available freelancers when no search
    : availableFilteredFreelancers; // Show filtered available results when searching

  const handleCardClick = () => {
    setIsExpanded(true);
    if (onCollectiveInvoiceClick) {
      onCollectiveInvoiceClick();
    }
  };

  const handleCollapseClick = () => {
    setIsExpanded(false);
  };

  const handleAddFriend = () => {
     // Check if no freelancers are selected
    if (selectedFreelancerIds.size === 0) {
      // Show error message
      setShowSearchError(true);
      return;
    }

    // Clear any error message
    setShowSearchError(false);

    // Add all selected freelancers
    if (selectedFreelancerIds.size > 0) {
      const newFriends: Friend[] = [];
      selectedFreelancerIds.forEach(freelancerId => {
        const freelancer = freelancers.find((f: any) => f.id?.toString() === freelancerId);
        if (freelancer) {
          const signUpUser = freelancer.signUpUser || {};
          const newFriend: Friend = {
            id: freelancer.id?.toString() || Date.now().toString(),
            userId: freelancer.userId || signUpUser.userId || signUpUser.id, // Add userId
            name: `${signUpUser.firstName || ''} ${signUpUser.lastName || ''}`.trim() || signUpUser.email || 'Unknown User',
            email: signUpUser.email || ''
          };
          // Check if friend doesn't already exist
          if (!friends.some(f => f.email === newFriend.email)) {
            newFriends.push(newFriend);
          }
        }
      });
      
      if (newFriends.length > 0) {
        setFriends([...friends, ...newFriends]);
        setSelectedFreelancerIds(new Set());
        setSearchValue('');
        setShowSuggestions(false);
      }
    }
    // Do not allow adding if no freelancer is selected from dropdown
    // User must select from the available freelancer list only
  };

  const handleAddFreelancer = (freelancer: any) => {
    const signUpUser = freelancer.signUpUser || {};
    const newFriend: Friend = {
      id: freelancer.id?.toString() || Date.now().toString(),
      userId: freelancer.userId || signUpUser.userId || signUpUser.id, // Add userId
      name: `${signUpUser.firstName || ''} ${signUpUser.lastName || ''}`.trim() || signUpUser.email || 'Unknown User',
      email: signUpUser.email || ''
    };
    // Check if friend already exists
    if (!friends.some(friend => friend.email === newFriend.email)) {
      setFriends([...friends, newFriend]);
    }
    setSearchValue('');
    setShowSuggestions(false);
  };

  const handleSearchFocus = () => {
    setShowSuggestions(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setShowSearchError(false); // Clear error when user types
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (freelancer: any, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    handleAddFreelancer(freelancer);
  };

  const handleFreelancerCheckboxChange = (freelancerId: string, event?: React.ChangeEvent<HTMLInputElement>) => {
    if (event) {
      event.stopPropagation();
    }
    const newSelected = new Set(selectedFreelancerIds);
    if (newSelected.has(freelancerId)) {
      newSelected.delete(freelancerId);
    } else {
      newSelected.add(freelancerId);
    }
    setSelectedFreelancerIds(newSelected);
  };

  const handleSelectAll = () => {
    const allIds = new Set(suggestionsToShow.map((f: any) => f.id?.toString()).filter(Boolean) as string[]);
    if (selectedFreelancerIds.size === allIds.size) {
      setSelectedFreelancerIds(new Set());
    } else {
      setSelectedFreelancerIds(allIds);
    }
  };

  const handleRemoveFriend = (friendId: string) => {
    // Prevent removal of the freelancer at index 0
    if (freelancers.length > 0 && friendId === freelancers[0].id?.toString()) {
      return;
    }
    setFriends(friends.filter(f => f.id !== friendId));
  };

  const handleInviteFriend = () => {
    setShowInviteModal(true);
  };

  const handleCloseInviteModal = () => {
    setShowInviteModal(false);
    setInviteEmail('');
  };

  // Email validation function
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleAddInvite = () => {
    if (inviteEmail.trim() && isValidEmail(inviteEmail) && !invitedFriends.includes(inviteEmail.trim())) {
      setInvitedFriends([...invitedFriends, inviteEmail.trim()]);
      setInviteEmail('');
    }
  };

  const handleRemoveInvite = (email: string) => {
    setInvitedFriends(invitedFriends.filter(f => f !== email));
    // Clear invite data from Redux state when removing emails
    dispatch(clearInviteState());
  };

  const handleSubmitInvites = useCallback(async () => {
    if (invitedFriends.length === 0) {
      toast.error('Please add at least one email to invite');
      return;
    }

    try {
      const result = await dispatch(submitInvite(invitedFriends));

      if (submitInvite.fulfilled.match(result)) {
        toast.success('Invitations sent successfully!');
        setInvitedFriends([]);
        handleCloseInviteModal();
      } else {
        // Show the specific error message from the API
        const errorMessage = result.error?.message || 'Failed to send invitations. Please try again.';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error sending invitations:', error);
      toast.error('Failed to send invitations. Please try again.');
    }
  }, [dispatch, invitedFriends]);

  if (isExpanded) {
    return (
      <>
        <Card className="invoice-card collective-invoice-card expanded mb-4">
          <Card.Body className="card-body">
            <div className="collective-expanded-content">
              {/* Header */}
              <div className="collective-header" onClick={handleCollapseClick} style={{ cursor: 'pointer' }}>
                <div className="header-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M16 12H8M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#E33B32" stroke-width="2" stroke-linecap="round" />
                  </svg>
                </div>
                <div className="header-text">
                  <h3>{t('CREATE_COLLECTIVE_INVOICE')}</h3>
                  <p>{t('ADD_FREELANCERS_WORKING_WITH_YOU')}</p>
                </div>
              </div>

              {/* Search and Invite Section */}
              <div className="search-and-invite-section">
                <div className="search-section">
                  <div 
                    ref={searchWrapperRef}
                    className="search-input-wrapper"
                    style={{ position: 'relative' }}
                    onMouseEnter={() => !isCustomerSelected && setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                  >
                    <Form.Control
                      type="text"
                      placeholder={t('SEARCH_FREELANCERS_BY_EMAIL_NAME')}
                      value={searchValue}
                      onChange={handleSearchChange}
                      onFocus={handleSearchFocus}
                      className="search-input"
                      disabled={!isCustomerSelected || isEditModeInvoice}
                    />
                    <Button
                      onClick={handleAddFriend}
                      className="add-button"
                      disabled={!isCustomerSelected || isEditModeInvoice}
                    >
                      {selectedFreelancerIds.size > 0 ? t('ADD_COUNT', { count: selectedFreelancerIds.size }) : t('ADD')}
                    </Button>
                     {/* Error message when trying to add without selection */}
                    {showSearchError && (
                      <div 
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: '0',
                          right: '0',
                          marginTop: '4px',
                          backgroundColor: '#FEE2E2',
                          color: '#DC2626',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          fontSize: '13px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          border: '1px solid #FCA5A5',
                          zIndex: 1000
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M8 5.33333V8M8 10.6667H8.00667M14.6667 8C14.6667 11.6819 11.6819 14.6667 8 14.6667C4.3181 14.6667 1.33333 11.6819 1.33333 8C1.33333 4.3181 4.3181 1.33333 8 1.33333C11.6819 1.33333 14.6667 4.3181 14.6667 8Z" stroke="#DC2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {t('INVITE_FREELANCER_FIRST_ERROR')}
                      </div>
                    )}
                    {/* Tooltip when no customer selected */}
                    {showTooltip && !isCustomerSelected && (
                      <div 
                        style={{
                          position: 'absolute',
                          top: '-45px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: '#333',
                          color: '#fff',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          fontSize: '14px',
                          whiteSpace: 'nowrap',
                          zIndex: 1000,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                        }}
                      >
                        {t('PLEASE_SELECT_CUSTOMER_FIRST')}
                        <div 
                          style={{
                            position: 'absolute',
                            bottom: '-5px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 0,
                            height: 0,
                            borderLeft: '6px solid transparent',
                            borderRight: '6px solid transparent',
                            borderTop: '6px solid #333'
                          }}
                        />
                      </div>
                    )}

                    {/* Search Results Dropdown */}
                    {shouldShowSuggestions && isCustomerSelected && (
                      <div className="search-results-dropdown">
                        {searchValue.trim() === '' && (
                          <div className="suggestions-header">
                            <span className="suggestions-title">{t('SUGGESTIONS')}</span>
                            {suggestionsToShow.length > 0 && (
                              <label className="select-all-checkbox" onMouseDown={(e) => e.preventDefault()}>
                                <input
                                  type="checkbox"
                                  checked={selectedFreelancerIds.size === suggestionsToShow.length && suggestionsToShow.length > 0}
                                  onChange={handleSelectAll}
                                  onMouseDown={(e) => e.stopPropagation()}
                                />
                                <span>{t('SELECT_ALL')}</span>
                              </label>
                            )}
                          </div>
                        )}
                        {suggestionsToShow.length > 0 && searchValue.trim() !== '' && (
                          <div className="suggestions-header">
                            <label className="select-all-checkbox" onMouseDown={(e) => e.preventDefault()}>
                              <input
                                type="checkbox"
                                checked={selectedFreelancerIds.size === suggestionsToShow.length}
                                onChange={handleSelectAll}
                                onMouseDown={(e) => e.stopPropagation()}
                              />
                              <span>{t('SELECT_ALL')}</span>
                            </label>
                          </div>
                        )}
                        {suggestionsToShow.map((freelancer: any) => (
                          <div
                            key={freelancer.id}
                            className="search-result-item"
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            <label className="freelancer-checkbox-label">
                              <input
                                type="checkbox"
                                checked={selectedFreelancerIds.has(freelancer.id?.toString())}
                                onChange={(e) => handleFreelancerCheckboxChange(freelancer.id?.toString(), e)}
                                onMouseDown={(e) => e.stopPropagation()}
                              />
                              <div className="freelancer-info">
                                <div className="freelancer-name">
                                  {`${freelancer.signUpUser?.firstName || ''} ${freelancer.signUpUser?.lastName || ''}`.trim() || freelancer.signUpUser?.email || 'Unknown User'}
                                </div>
                                <div className="freelancer-email">{freelancer.signUpUser?.email || 'No email'}</div>
                              </div>
                            </label>
                          </div>
                        ))}
                        {suggestionsToShow.length === 0 && searchValue.trim() !== '' && (
                          <div className="no-results">
                            <span>{t('NO_FREELANCERS_FOUND', { searchValue })}</span>
                          </div>
                        )}
                        {suggestionsToShow.length === 0 && searchValue.trim() === '' && (
                          <div className="no-results">
                            <span>{t('NO_FREELANCERS_AVAILABLE')}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Friends Tags */}
                 <div className="friends-section" style={{ marginTop: showSearchError ? '48px' : '0' }}>
                    {friends
                      .filter((friend, index) => {
                        // Hide the freelancer at index 0 from the badge display
                        // Compare with the actual index 0 freelancer from freelancers list
                        if (freelancers.length > 0) {
                          const firstFreelancer = freelancers[0];
                          const firstFreelancerUserId = firstFreelancer.userId || firstFreelancer.signUpUser?.userId || firstFreelancer.signUpUser?.id;
                          const firstFreelancerEmail = firstFreelancer.signUpUser?.email;
                          
                          // Check if current friend matches index 0 freelancer
                          return !(friend.userId === firstFreelancerUserId || friend.email === firstFreelancerEmail);
                        }
                        return true;
                      })
                      .map((friend) => (
                        <div key={friend.id} className="friend-tag">
                          <span className="friend-name">{friend.name}</span>
                          <button
                            className="remove-friend-btn"
                            onClick={() => handleRemoveFriend(friend.id)}
                            disabled={isEditModeInvoice}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M16 12H8M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#E33B32" stroke-width="2" stroke-linecap="round" />
                            </svg>
                          </button>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Invite Section */}
                <div className="invite-section">
                  <div className="invite-content">
                    <div className="invite-icon">
                      <svg
                        width={54}
                        height={56}
                        viewBox="0 0 54 56"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        xmlnsXlink="http://www.w3.org/1999/xlink"
                      >
                        <rect width={54} height={56} fill="url(#pattern0_8788_91071)" />
                        <defs>
                          <pattern
                            id="pattern0_8788_91071"
                            patternContentUnits="objectBoundingBox"
                            width={1}
                            height={1}
                          >
                            <use
                              xlinkHref="#image0_8788_91071"
                              transform="matrix(0.00810185 0 0 0.0078125 -0.0185185 0)"
                            />
                          </pattern>
                          <image
                            id="image0_8788_91071"
                            width={128}
                            height={128}
                            preserveAspectRatio="none"
                            xlinkHref="data:image/png;base64,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"
                          />
                        </defs>
                      </svg>

                    </div>
                    <div className="invite-text">
                      <p>{t('INVITE_FREELANCERS_TO_INVOZIO')}</p>
                    </div>
                    <div className='invite-friend-btn-main-dev'>
                    <Button
                      className="invite-friend-btn"
                      onClick={handleInviteFriend}
                      disabled={isEditModeInvoice}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <g clip-path="url(#clip0_9423_2591)">
                          <path d="M12.6477 8.66341L14.4888 6.8688C14.6866 6.67595 14.5775 6.33996 14.3041 6.30024L10.3267 5.72229C10.2181 5.70651 10.1243 5.63832 10.0757 5.53994L8.29695 1.93581C8.17468 1.68806 7.8214 1.68806 7.69913 1.93581L5.92039 5.53994C5.87183 5.63832 5.77798 5.70651 5.66941 5.72229L1.69201 6.30024C1.41861 6.33996 1.30944 6.67595 1.50728 6.8688L4.38534 9.67422C4.46391 9.7508 4.49976 9.86114 4.48121 9.96927L3.80179 13.9306C3.75509 14.2029 4.0409 14.4105 4.28544 14.282L7.84293 12.4117C7.94004 12.3606 8.05605 12.3606 8.15315 12.4117L9.33138 13.0311M11.998 13.3301H13.3314M13.3314 13.3301H14.6647M13.3314 13.3301V11.9968M13.3314 13.3301L13.3314 14.6635" stroke="white" stroke-width="2" stroke-miterlimit="10" stroke-linecap="round" />
                        </g>
                        <defs>
                          <clipPath id="clip0_9423_2591">
                            <rect width="16" height="16" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                      {t('INVITE_FREELANCERS')}
                    </Button>
                    </div>
                  </div>
                </div>
              </div>


            </div>
          </Card.Body>
        </Card>

        {/* Invite Friends Modal */}
        <Modal show={showInviteModal} onHide={handleCloseInviteModal} centered className="invite-friends-modal">
          <Modal.Header className="invite-modal-header">
            <Modal.Title className="invite-modal-title">{t('INVITE_FREELANCERS')}</Modal.Title>
            <button className="invite-modal-close" onClick={handleCloseInviteModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </Modal.Header>

          <Modal.Body className="invite-modal-body">
            <p className="invite-modal-description">
              {t('INVITE_FREELANCERS_WORKING_WITH_YOU')}
            </p>

            {/* Invited Friends List */}
            {invitedFriends.map((email, index) => (
              <div key={index} className="invited-friend-item">
                <span className="invited-friend-email">{email}</span>
                <button className="remove-invite-btn" onClick={() => handleRemoveInvite(email)}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M16 12H8M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#E33B32" stroke-width="2" stroke-linecap="round" />
                  </svg>
                </button>
              </div>
            ))}

            {/* Email Input */}
            <div className="invite-input-section">
              <label className="invite-input-label">{t('INVITE_FREELANCER')}</label>
              <div className="invite-input-wrapper">
                <Form.Control
                  type="email"
                  placeholder={t('ENTER_FREELANCER_EMAIL')}
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="invite-email-input"
                />
                <svg className="email-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" />
                  <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" />
                </svg>
                <Button 
                  className="invite-add-btn" 
                  onClick={handleAddInvite}
                  disabled={!inviteEmail.trim() || !isValidEmail(inviteEmail)}
                >
                  {t('ADD')}
                </Button>
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer className="invite-modal-footer">
            <Button variant="outline-primary" className="invite-cancel-btn" onClick={handleCloseInviteModal} disabled={inviteLoading}>
              {t('CANCEL')}
            </Button>
            <Button variant="primary" className="invite-submit-btn" onClick={handleSubmitInvites} disabled={inviteLoading}>
              {inviteLoading ? t('SENDING') : t('SUBMIT')}
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  return (
    <>
      <Card className="invoice-card collective-invoice-card mb-4" onClick={handleCardClick}>
        <Card.Body className="card-body">
          <div className="collective-invoice-content">
            <div className="collective-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#6750B8" strokeWidth="2" />
                <path d="M12 8v8M8 12h8" stroke="#6750B8" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
            <div className="collective-text">
              <h3>{t('CREATE_COLLECTIVE_INVOICE')}</h3>
              <p style={{color: "var(--text-icons-dark-disabled, #68727D)", fontFamily: "Plus Jakarta Sans", fontSize: "16px", fontStyle: "normal", fontWeight: "500", lineHeight: "140%"}}>
                {t('ADD_FRIENDS_WORKING_WITH_YOU')}</p>
            </div>
          </div>
        </Card.Body>
      </Card>
 
      {/* Invite Friends Modal */}
      <Modal show={showInviteModal} onHide={handleCloseInviteModal} centered className="invite-friends-modal">
        <Modal.Header className="invite-modal-header">
          <Modal.Title className="invite-modal-title">{t('INVITE_FREELANCERS')}</Modal.Title>
          <button className="invite-modal-close" onClick={handleCloseInviteModal}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </Modal.Header>

        <Modal.Body className="invite-modal-body">
          <p className="invite-modal-description">
            {t('INVITE_FRIENDS_WORKING_WITH_YOU')}
          </p>

          {/* Invited Friends List */}
          {invitedFriends.map((email, index) => (
            <div key={index} className="invited-friend-item">
              <span className="invited-friend-email">{email}</span>
              <button className="remove-invite-btn" onClick={() => handleRemoveInvite(email)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#E33B32" />
                  <path d="M15 9l-6 6M9 9l6 6" stroke="white" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          ))}

          {/* Email Input */}
          <div className="invite-input-section">
            <label className="invite-input-label">{t('INVITE_FREELANCER')}</label>
            <div className="invite-input-wrapper">
              <Form.Control
                type="email"
                placeholder={t('ENTER_FREELANCER_EMAIL')}
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="invite-email-input"
              />
              <svg className="email-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9 2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" />
                <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" />
              </svg>
              <Button 
                className="invite-add-btn" 
                onClick={handleAddInvite}
                disabled={!inviteEmail.trim() || !isValidEmail(inviteEmail)}
              >
                {t('ADD')}
              </Button>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="invite-modal-footer">
          <Button variant="outline-primary" className="invite-cancel-btn" onClick={handleCloseInviteModal} disabled={inviteLoading}>
            {t('CANCEL')}
          </Button>
          <Button variant="primary" className="invite-submit-btn" onClick={handleSubmitInvites} disabled={inviteLoading}>
            {inviteLoading ? t('SENDING') : t('SUBMIT')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CollectiveInvoice;